import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import ContactModal from '../../components/feature/ContactModal';
import { Link } from 'react-router-dom';
import { useState } from 'react';

export default function About() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const milestones = [
    { year: '1994', title: 'Company Founded', description: 'Olatunde International Limited established with a vision to transform Nigerian cocoa export' },
    { year: '2000', title: 'First Major Export', description: 'Successfully exported our first 10,000 MT of premium cocoa to European markets' },
    { year: '2010', title: 'Sustainability Focus', description: 'Launched comprehensive farmer training and sustainability programs' },
    { year: '2018', title: 'Certification Achieved', description: 'Became Rainforest Alliance certified with full supply chain traceability' },
    { year: '2022', title: 'EUDR Compliance', description: 'Achieved full EUDR compliance ahead of regulation requirements' },
    { year: '2024', title: '100,000 MT Capacity', description: 'Reached annual export capacity of over 100,000 metric tons' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-32 bg-gray-900">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/ee7d0f3d5dd5d8644a11a0562535fbc0/22f8527f4c3f33bdb393cb9ff2c7769b.png"
            alt="Our Journey: 1994 - Today"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Our Story Since 1994
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed">
            Three decades of excellence in Nigerian cocoa export, built on a foundation 
            of integrity, sustainability, and unwavering commitment to farmer prosperity.
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-10 rounded-2xl">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-compass-line text-2xl text-white"></i>
              </div>
              <h2 className="text-3xl font-bold text-green-800 mb-6">Our Mission</h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                To deliver ethically sourced, traceable Nigerian cocoa while advancing farmer prosperity 
                and sustainable agriculture. We are committed to building transparent supply chains that 
                benefit every stakeholder from farm to final consumer.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-10 rounded-2xl">
              <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-eye-line text-2xl text-white"></i>
              </div>
              <h2 className="text-3xl font-bold text-amber-800 mb-6">Our Vision</h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                To lead Africa's cocoa export industry through sustainability, integrity, and a commitment 
                to empowering communities and protecting the environment. We envision a future where 
                Nigerian cocoa sets the global standard for quality and ethical sourcing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-green-800 mb-6">
                Premier Nigerian Cocoa Export Company
              </h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  Olatunde International Limited stands as Nigeria's premier cocoa export company, 
                  with a proud legacy established in 1994. Located in the heart of Nigeria's cocoa-growing 
                  rainforest belt, we have built our reputation on delivering exceptional quality and 
                  maintaining the highest standards of ethical sourcing.
                </p>
                <p>
                  Our strategic position in Akure, Ondo State, provides us with direct access to some 
                  of the world's finest cocoa-growing regions. This proximity to origin allows us to 
                  work closely with farmers, ensuring quality control from the very beginning of the 
                  supply chain.
                </p>
                <p>
                  With over three decades of experience, we have developed robust relationships with 
                  over 10,000 farmers, cooperatives, and international buyers. Our commitment to transparency, 
                  sustainability, and quality has made us a trusted partner for cocoa processors, 
                  traders, and specialty buyers worldwide.
                </p>
              </div>
              <div className="mt-8">
                <Link to="/services">
                  <Button variant="primary" size="lg">
                    Explore Our Services
                    <div className="w-5 h-5 flex items-center justify-center ml-2">
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-lg text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-calendar-line text-xl text-green-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-green-800">30+</h3>
                <p className="text-gray-600">Years of Excellence</p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-lg text-center">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-truck-line text-xl text-amber-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-green-800">100,000+</h3>
                <p className="text-gray-600">MT Annual Capacity</p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-lg text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-group-line text-xl text-green-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-green-800">10,000+</h3>
                <p className="text-gray-600">Partner Farmers</p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-lg text-center">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-global-line text-xl text-amber-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-green-800">15+</h3>
                <p className="text-gray-600">Countries Served</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-green-800 mb-4">Our Journey Through Time</h2>
            <p className="text-xl text-gray-600">Three decades of growth, innovation, and commitment to excellence</p>
          </div>
          
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-green-200"></div>
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                    <div className="bg-white p-6 rounded-xl shadow-lg">
                      <div className="text-2xl font-bold text-amber-600 mb-2">{milestone.year}</div>
                      <h3 className="text-xl font-semibold text-green-800 mb-3">{milestone.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{milestone.description}</p>
                    </div>
                  </div>
                  
                  <div className="relative z-10">
                    <div className="w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center">
                      <div className="w-3 h-3 bg-white rounded-full"></div>
                    </div>
                  </div>
                  
                  <div className="w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-green-700">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Partner with Us?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-3xl mx-auto">
            Join our network of satisfied clients and experience the quality, reliability, 
            and ethical sourcing that has defined our company for over 30 years.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/services">
              <Button variant="primary" size="lg" className="bg-amber-600 hover:bg-amber-700">
                Our Services
              </Button>
            </Link>
            <Button 
              variant="outline" 
              size="lg" 
              className="border-white text-white hover:bg-white hover:text-green-800"
              onClick={() => setIsContactModalOpen(true)}
            >
              Contact Us Today
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
      />
    </div>
  );
}
